/**
 * Settings > Integrations — Wildix webhook secret, Presence WebSocket URL (PRD Phase 2).
 */
import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';

export default function SettingsIntegrations() {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wildixSecret, setWildixSecret] = useState('');
  const [wildixConfigured, setWildixConfigured] = useState(false);
  const [presenceWsUrl, setPresenceWsUrl] = useState('');
  const [callHistoryRetentionDays, setCallHistoryRetentionDays] = useState(1);
  const [agentStatusPollSeconds, setAgentStatusPollSeconds] = useState(5);
  const [presenceTestLoading, setPresenceTestLoading] = useState(false);
  const [presenceTestResult, setPresenceTestResult] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      try {
        const base = typeof window !== 'undefined' ? window.location.origin : '';
        const res = await fetch(`${base}/api/config?keys=wildix_webhook_secret,presence_ws_url,call_history_retention_days,agent_status_poll_interval_seconds`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.wildix_webhook_secret === '********') setWildixConfigured(true);
          if (typeof data.presence_ws_url === 'string' && data.presence_ws_url) {
            setPresenceWsUrl(data.presence_ws_url);
          }
          const retention = parseInt(data.call_history_retention_days ?? '1', 10);
          if (retention >= 1 && retention <= 7) setCallHistoryRetentionDays(retention);
          const poll = parseInt(data.agent_status_poll_interval_seconds ?? '5', 10);
          if (poll >= 5 && poll <= 3600) setAgentStatusPollSeconds(poll);
        }
      } catch {
        // keep defaults
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  const apiBase = typeof window !== 'undefined' ? window.location.origin : '';
  const workerOrigin = (() => {
    const ws = presenceWsUrl.trim();
    if (!ws.startsWith('ws')) return '';
    try {
      const normalized = ws.startsWith('wss://')
        ? `https://${ws.slice('wss://'.length)}`
        : `http://${ws.slice('ws://'.length)}`;
      return new URL(normalized).origin;
    } catch {
      return '';
    }
  })();

  const save = async (payload: {
    wildix_webhook_secret?: string;
    presence_ws_url?: string;
    call_history_retention_days?: number;
    agent_status_poll_interval_seconds?: number;
  }) => {
    const token = await getToken();
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      if (payload.wildix_webhook_secret !== undefined) {
        setWildixConfigured(true);
        setWildixSecret('');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWildix = async () => {
    if (wildixSecret.trim()) await save({ wildix_webhook_secret: wildixSecret.trim() });
  };

  const handleSavePresence = async () => {
    await save({ presence_ws_url: presenceWsUrl.trim() || '' });
  };

  const handleSaveCallHistoryRetention = async () => {
    await save({ call_history_retention_days: callHistoryRetentionDays });
  };

  const handleSaveAgentStatusPoll = async () => {
    await save({ agent_status_poll_interval_seconds: agentStatusPollSeconds });
  };

  const runPresenceTest = async () => {
    if (!workerOrigin) {
      setError('Set a valid presence_ws_url first (wss://.../ws).');
      return;
    }
    setPresenceTestLoading(true);
    setPresenceTestResult(null);
    setError(null);
    try {
      const res = await fetch(`${workerOrigin}/presence/test-sync`, {
        method: 'POST',
      });
      const raw = await res.text();
      let formatted = raw;
      try {
        formatted = JSON.stringify(JSON.parse(raw), null, 2);
      } catch {
        /* leave as text */
      }
      setPresenceTestResult(formatted);
      let json: { ok?: boolean; reason?: string; error?: string } = {};
      try {
        json = JSON.parse(raw) as typeof json;
      } catch {
        /* non-json body */
      }
      if (!res.ok) {
        setError(json.reason || json.error || `HTTP ${res.status}`);
      } else if (json.ok === false) {
        setError(json.reason || json.error || 'UC sync reported failure');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Presence test failed');
    } finally {
      setPresenceTestLoading(false);
    }
  };

  if (loading) {
    return <p style={{ color: 'var(--text-muted)' }}>Loading…</p>;
  }

  return (
    <div>
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>Wildix</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1rem' }}>
          Webhook secret from Wildix WMS (PBX → Integrations → Cloud integrations). Configure the webhook URL in Wildix to:{' '}
          <code style={{ background: 'rgba(255,255,255,0.08)', padding: '0.2em 0.4em', borderRadius: 4 }}>
            https://ipscape-wallboards-presence.&lt;your-account&gt;.workers.dev/webhooks/wildix
          </code>
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>
          <input
            type="password"
            value={wildixSecret}
            onChange={(e) => setWildixSecret(e.target.value)}
            placeholder={wildixConfigured ? 'Leave blank to keep current secret' : 'Enter webhook secret'}
            autoComplete="off"
            style={{
              padding: '0.5rem 0.75rem',
              minWidth: 240,
              background: 'var(--widget-bg)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 6,
              color: 'var(--text)',
            }}
          />
          <button
            type="button"
            onClick={handleSaveWildix}
            disabled={saving || !wildixSecret.trim()}
            style={{
              padding: '0.5rem 1rem',
              background: wildixSecret.trim() ? 'var(--accent)' : 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: 6,
              color: 'var(--text)',
              cursor: wildixSecret.trim() && !saving ? 'pointer' : 'not-allowed',
            }}
          >
            {saving ? 'Saving…' : 'Save secret'}
          </button>
          {wildixConfigured && (
            <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Secret configured</span>
          )}
        </div>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>Presence (real-time Status)</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1rem' }}>
          WebSocket URL for the Status page to receive live agent presence. Example:{' '}
          <code style={{ background: 'rgba(255,255,255,0.08)', padding: '0.2em 0.4em', borderRadius: 4 }}>
            wss://ipscape-wallboards-presence.&lt;your-account&gt;.workers.dev/ws
          </code>
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>
          <input
            type="url"
            value={presenceWsUrl}
            onChange={(e) => setPresenceWsUrl(e.target.value)}
            placeholder="wss://…"
            autoComplete="off"
            style={{
              padding: '0.5rem 0.75rem',
              minWidth: 360,
              background: 'var(--widget-bg)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 6,
              color: 'var(--text)',
            }}
          />
          <button
            type="button"
            onClick={handleSavePresence}
            disabled={saving}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--accent)',
              border: 'none',
              borderRadius: 6,
              color: 'var(--text)',
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : 'Save URL'}
          </button>
        </div>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>ipSCAPE UC — agent status poll</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1rem' }}>
          How often the Presence worker calls UC <code style={{ background: 'rgba(255,255,255,0.08)', padding: '0.15em 0.35em', borderRadius: 4 }}>GET /agent-state</code>{' '}
          (Bearer). Independent of other Platform API polling. Minimum <strong>5</strong> seconds; default <strong>5</strong>. Requires UC login secrets on the Worker (
          <code style={{ background: 'rgba(255,255,255,0.08)', padding: '0.15em 0.35em', borderRadius: 4 }}>IPSCAPE_UC_API_USERNAME</code> /{' '}
          <code style={{ background: 'rgba(255,255,255,0.08)', padding: '0.15em 0.35em', borderRadius: 4 }}>IPSCAPE_UC_API_PASSWORD</code>). See docs/ipscape-uc-realtime-and-campaigns.md.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
            <span style={{ color: 'var(--text-muted)' }}>Interval (seconds)</span>
            <input
              type="number"
              min={5}
              max={3600}
              step={1}
              value={agentStatusPollSeconds}
              onChange={(e) => setAgentStatusPollSeconds(parseInt(e.target.value, 10) || 5)}
              style={{
                width: 100,
                padding: '0.5rem 0.75rem',
                background: 'var(--widget-bg)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 6,
                color: 'var(--text)',
              }}
            />
          </label>
          <button
            type="button"
            onClick={handleSaveAgentStatusPoll}
            disabled={saving || agentStatusPollSeconds < 5 || agentStatusPollSeconds > 3600}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--accent)',
              border: 'none',
              borderRadius: 6,
              color: 'var(--text)',
              cursor: saving || agentStatusPollSeconds < 5 ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
        <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <h3 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>Test UC agent-state (Super Admin)</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '0.75rem' }}>
            Runs a <strong>one-off</strong> UC <code style={{ background: 'rgba(255,255,255,0.08)', padding: '0.15em 0.35em', borderRadius: 4 }}>GET /agent-state</code> via the Presence worker,{' '}
            <strong>bypassing operating hours and the “enable polling” app flag</strong> for this call only. Uses UC credentials on the Worker (
            <code style={{ background: 'rgba(255,255,255,0.08)', padding: '0.15em 0.35em', borderRadius: 4 }}>IPSCAPE_UC_*</code>). Response should return in about a second; live tiles update right after via WebSocket.
          </p>
          <button
            type="button"
            onClick={runPresenceTest}
            disabled={saving || presenceTestLoading}
            style={{
              padding: '0.5rem 1rem',
              background: 'rgba(59,130,246,0.25)',
              border: '1px solid rgba(59,130,246,0.45)',
              borderRadius: 6,
              color: 'var(--text)',
              cursor: presenceTestLoading ? 'not-allowed' : 'pointer',
              marginBottom: '0.75rem',
            }}
          >
            {presenceTestLoading ? 'Running test…' : 'Run test now'}
          </button>
          {presenceTestResult && (
            <pre
              style={{
                margin: 0,
                padding: '0.75rem',
                fontSize: '0.75rem',
                maxHeight: 280,
                overflow: 'auto',
                background: 'var(--bg)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 6,
                color: 'var(--text-muted)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {presenceTestResult}
            </pre>
          )}
        </div>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>Call history</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1rem' }}>
          How long to keep call records (1–7 days). A cron job purges older rows. Default: 1 day.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>
          <select
            value={callHistoryRetentionDays}
            onChange={(e) => setCallHistoryRetentionDays(parseInt(e.target.value, 10))}
            style={{
              padding: '0.5rem 0.75rem',
              background: 'var(--widget-bg)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 6,
              color: 'var(--text)',
            }}
          >
            {[1, 2, 3, 4, 5, 6, 7].map((d) => (
              <option key={d} value={d}>{d} day{d !== 1 ? 's' : ''}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleSaveCallHistoryRetention}
            disabled={saving}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--accent)',
              border: 'none',
              borderRadius: 6,
              color: 'var(--text)',
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </section>

      {error && (
        <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '1rem' }}>{error}</p>
      )}
    </div>
  );
}
